import React from 'react';
import { View, ScrollView, Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import Icon from '@/components/Icon';
import MetaHead from '@/components/MetaHead';
import useResponsiveness from '@/hooks/useResponsiveness';
import { isDarkTheme } from '@/constants/Colors';
import PublicFooter from '@/components/PublicFooter';
import FlowField from '@/components/Landing/FlowField';

const SectionHeading = ({ eyebrow, title, subtitle, maxWidth = 680 }) => {
  const { isXS, isSM } = useResponsiveness();
  const { theme } = useTheme();
  const isDark = isDarkTheme(theme);

  return (
    <View style={{ alignItems: 'center', marginBottom: isXS ? 40 : 64 }}>
      {eyebrow && (
        <Text
          size="s"
          weight="medium"
          style={{
            color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
            textTransform: 'uppercase',
            letterSpacing: 1.5,
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          {eyebrow}
        </Text>
      )}
      <Text
        weight="bold"
        style={{
          fontSize: isXS ? 32 : isSM ? 40 : 52,
          textAlign: 'center',
          marginBottom: subtitle ? 16 : 0,
          lineHeight: isXS ? 38 : isSM ? 48 : 60,
          letterSpacing: isXS ? -0.8 : -1.5,
        }}
      >
        {title}
      </Text>
      {subtitle && (
        <Text
          size="l"
          color="secondary"
          style={{
            textAlign: 'center',
            maxWidth,
            lineHeight: isXS ? 26 : 30,
            fontSize: isXS ? 16 : 19,
          }}
        >
          {subtitle}
        </Text>
      )}
    </View>
  );
};

const IMPORT_FEATURES = [
  {
    icon: 'comments',
    title: 'Import Conversations',
    desc: 'Bring your entire chat history from ChatGPT and Claude. Every conversation, every thread — transferred in minutes.',
  },
  {
    icon: 'brain',
    title: 'Import Memories',
    desc: 'Transfer what your old AI learned about you — your preferences, projects, and context — so Gab AI knows you from day one.',
  },
  {
    icon: 'clock-rotate-left',
    title: 'Nothing Left Behind',
    desc: 'Your months of context, corrections, and personalization don\'t disappear. Pick up right where you left off.',
  },
];

const PRIVACY_FEATURES = [
  {
    icon: 'user-secret',
    title: 'Anonymous by Design',
    desc: 'Every request to third-party AI models is completely anonymized. No one can trace responses back to you — ever.',
  },
  {
    icon: 'ban',
    title: 'We Never Train on Your Data',
    desc: 'Your conversations are never used to train AI models. Period. What you say stays between you and your AI.',
  },
  {
    icon: 'eye-slash',
    title: 'No Surveillance, No Ads',
    desc: 'We don\'t profile you, track your behavior, or sell your data to advertisers. Your AI is yours alone.',
  },
];

const ALL_IN_ONE_FEATURES = [
  { icon: 'pen', label: 'Writing', desc: 'Articles, emails, code' },
  { icon: 'image', label: 'Images', desc: 'AI art & photos' },
  { icon: 'video', label: 'Video', desc: 'Clips & animations' },
  { icon: 'music', label: 'Music', desc: 'Songs & sounds' },
  { icon: 'globe', label: 'Web Search', desc: 'Real-time results' },
  { icon: 'microphone', label: 'Voice', desc: 'Speak naturally' },
  { icon: 'brain', label: 'Memory', desc: 'Remembers you' },
  { icon: 'robot', label: 'Agents', desc: 'Custom assistants' },
];

const COMPARISON_ROWS = [
  { feature: 'Every top AI model in one place', gab: true, others: false },
  { feature: 'Generate images, video, and music', gab: true, others: false },
  { feature: 'Anonymized conversations', gab: true, others: false },
  { feature: 'Never trains on your data', gab: true, others: false },
  { feature: 'Import conversations & memories', gab: true, others: false },
  { feature: 'Natural real-time voice conversations', gab: true, others: false },
  { feature: 'Automated scheduled tasks', gab: true, others: false },
  { feature: 'Multi-model responses', gab: true, others: false },
];

export default function SwitchPage() {
  const { colors, theme } = useTheme();
  const { isXS, isSM } = useResponsiveness();
  const router = useRouter();
  const isDark = isDarkTheme(theme);

  const sectionPadding = isXS ? 20 : 40;
  const sectionVertical = isXS ? 64 : 120;

  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)';
  const cardBorder = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
  const iconBoxBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
  const iconColor = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)';
  const subtleText = isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)';

  return (
    <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }}>
      <FlowField isDark={isDark} />
      <MetaHead
        title="Switch to Gab AI — Transfer Everything, Lose Nothing"
        description="Switch from ChatGPT, Claude, or Gemini to Gab AI without starting over. Import your conversations and memories. Restore your privacy. Access every AI model in one place."
        canonical="/switch"
        keywords="switch to Gab AI, ChatGPT alternative, Claude alternative, import AI conversations, private AI, all-in-one AI"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Switch to Gab AI',
          description: 'Transfer your conversations and memories from other AI platforms to Gab AI. Restore your privacy with the all-in-one AI.',
          url: 'https://gab.ai/switch',
        }}
      />

      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        style={{ position: 'relative', zIndex: 1 }}
      >
        {/* ================================================================ */}
        {/* HERO */}
        {/* ================================================================ */}
        <View
          style={{
            width: '100%',
            alignItems: 'center',
            paddingHorizontal: sectionPadding,
            paddingTop: isXS ? 100 : 140,
            paddingBottom: isXS ? 60 : 80,
          }}
        >
          <View style={{ maxWidth: 840, alignItems: 'center' }}>
            <Text
              size="s"
              weight="medium"
              style={{
                color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
                textTransform: 'uppercase',
                letterSpacing: 2,
                marginBottom: 24,
                fontSize: 13,
              }}
            >
              Switch to Gab AI
            </Text>

            <Text
              weight="bold"
              style={{
                fontSize: isXS ? 36 : isSM ? 48 : 64,
                textAlign: 'center',
                marginBottom: 24,
                lineHeight: isXS ? 44 : isSM ? 56 : 72,
                letterSpacing: -1.5,
              }}
            >
              Transfer Everything.{'\n'}Lose Nothing.
            </Text>

            <Text
              size="l"
              color="secondary"
              style={{
                textAlign: 'center',
                fontSize: isXS ? 17 : 20,
                lineHeight: isXS ? 28 : 32,
                maxWidth: 680,
                marginBottom: isXS ? 36 : 48,
              }}
            >
              Bring your conversations and memories from ChatGPT, Claude, or any AI provider.
              Your first conversation with Gab AI will feel like your hundredth.
            </Text>

            <View
              style={{
                flexDirection: isXS ? 'column' : 'row',
                gap: 12,
                marginBottom: 20,
              }}
            >
              <Button
                text="Start Switching"
                variant="purple"
                size="large"
                isRound
                prependIcon="arrow-right-to-bracket"
                iconIsSolid
                onPress={() => router.push('/signup')}
                style={{ paddingHorizontal: isXS ? 32 : 48, height: isXS ? 52 : 56 }}
                textStyle={{ fontSize: isXS ? 16 : 17, fontWeight: '600' }}
              />
              <Button
                text="See What's Different"
                variant="secondary"
                size="large"
                isRound
                onPress={() => {
                  const el = document.getElementById('section-why-switch');
                  el?.scrollIntoView({ behavior: 'smooth' });
                }}
                style={{
                  paddingHorizontal: isXS ? 32 : 48,
                  height: isXS ? 52 : 56,
                  backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                }}
              />
            </View>
          </View>
        </View>

        {/* ================================================================ */}
        {/* HOW SWITCHING WORKS */}
        {/* ================================================================ */}
        <View
          style={{
            width: '100%',
            alignItems: 'center',
            paddingHorizontal: sectionPadding,
            paddingVertical: sectionVertical,
          }}
        >
          <View style={{ maxWidth: 1000, width: '100%', alignItems: 'center' }}>
            <SectionHeading
              eyebrow="Seamless Transfer"
              title={"Switch in Minutes,\nNot Days"}
              subtitle="We built tools that make transferring your AI life effortless. Import your conversations and memories with just a few clicks."
            />

            <View
              style={{
                flexDirection: isXS ? 'column' : 'row',
                gap: isXS ? 16 : 20,
                width: '100%',
                marginBottom: isXS ? 48 : 64,
              }}
            >
              {IMPORT_FEATURES.map((feature, index) => (
                <View
                  key={index}
                  style={{
                    flex: isXS ? undefined : 1,
                    padding: isXS ? 24 : 28,
                    borderRadius: 20,
                    backgroundColor: cardBg,
                    borderWidth: 1,
                    borderColor: cardBorder,
                  }}
                >
                  <View
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      backgroundColor: iconBoxBg,
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 20,
                    }}
                  >
                    <Icon name={feature.icon} size={20} color={iconColor} solid />
                  </View>
                  <Text weight="semibold" style={{ fontSize: 18, marginBottom: 8 }}>
                    {feature.title}
                  </Text>
                  <Text size="n" color="secondary" style={{ lineHeight: 24 }}>
                    {feature.desc}
                  </Text>
                </View>
              ))}
            </View>

            {/* How it works steps */}
            <View
              style={{
                width: '100%',
                flexDirection: isXS ? 'column' : 'row',
                gap: isXS ? 32 : 48,
                marginBottom: isXS ? 48 : 64,
              }}
            >
              {[
                { step: '1', title: 'Create Your Account', desc: 'Sign up for Gab AI in seconds. Free accounts include generous credits to get started.' },
                { step: '2', title: 'Import Your Data', desc: 'Use our import tools to bring your conversations from Gab AI, ChatGPT, or Claude, and transfer your memories with one prompt.' },
                { step: '3', title: 'Pick Up Where You Left Off', desc: 'Your AI already knows you. Start chatting with full context, preferences, and history intact.' },
              ].map((item, index) => (
                <View key={index} style={{ flex: isXS ? undefined : 1, alignItems: 'center' }}>
                  <Text
                    weight="bold"
                    style={{
                      fontSize: 48,
                      color: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)',
                      marginBottom: 12,
                    }}
                  >
                    {item.step}
                  </Text>
                  <Text weight="semibold" style={{ fontSize: 17, marginBottom: 8, textAlign: 'center' }}>
                    {item.title}
                  </Text>
                  <Text size="n" color="secondary" style={{ textAlign: 'center', lineHeight: 24 }}>
                    {item.desc}
                  </Text>
                </View>
              ))}
            </View>

            <Button
              text="Start Your Transfer"
              variant="purple"
              size="large"
              isRound
              onPress={() => router.push('/signup')}
              style={{ paddingHorizontal: isXS ? 32 : 48 }}
            />

            {/* Documentation links */}
            <View
              style={{
                flexDirection: isXS ? 'column' : 'row',
                gap: isXS ? 12 : 24,
                marginTop: isXS ? 24 : 32,
                alignItems: 'center',
              }}
            >
              <Pressable
                onPress={() => router.push('/docs/memory')}
                style={({ hovered }) => ({
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                  opacity: hovered ? 0.7 : 1,
                  transition: 'opacity 0.15s ease',
                })}
              >
                <Icon name="book" size={14} color={subtleText} />
                <Text size="s" color="secondary" style={{ textDecorationLine: 'underline' }}>
                  How to import your memories
                </Text>
              </Pressable>
              <Pressable
                onPress={() => router.push('/docs/import-chats')}
                style={({ hovered }) => ({
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                  opacity: hovered ? 0.7 : 1,
                  transition: 'opacity 0.15s ease',
                })}
              >
                <Icon name="book" size={14} color={subtleText} />
                <Text size="s" color="secondary" style={{ textDecorationLine: 'underline' }}>
                  How to import your conversations
                </Text>
              </Pressable>
            </View>
          </View>
        </View>

        {/* ================================================================ */}
        {/* RESTORE YOUR PRIVACY */}
        {/* ================================================================ */}
        <View
          nativeID="section-why-switch"
          style={{
            width: '100%',
            alignItems: 'center',
            paddingHorizontal: sectionPadding,
            paddingVertical: sectionVertical,
          }}
        >
          <View style={{ maxWidth: 1000, width: '100%', alignItems: 'center' }}>
            <SectionHeading
              eyebrow="Privacy First"
              title={"Restore Your Privacy"}
              subtitle="Big Tech AI platforms harvest your conversations to train their models and target you with ads. Gab AI was built from the ground up to protect you."
            />

            <View
              style={{
                flexDirection: isXS ? 'column' : 'row',
                gap: isXS ? 16 : 20,
                width: '100%',
                marginBottom: isXS ? 48 : 64,
              }}
            >
              {PRIVACY_FEATURES.map((feature, index) => (
                <View
                  key={index}
                  style={{
                    flex: isXS ? undefined : 1,
                    padding: isXS ? 24 : 28,
                    borderRadius: 20,
                    backgroundColor: cardBg,
                    borderWidth: 1,
                    borderColor: cardBorder,
                  }}
                >
                  <View
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      backgroundColor: iconBoxBg,
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 20,
                    }}
                  >
                    <Icon name={feature.icon} size={20} color={iconColor} solid />
                  </View>
                  <Text weight="semibold" style={{ fontSize: 18, marginBottom: 8 }}>
                    {feature.title}
                  </Text>
                  <Text size="n" color="secondary" style={{ lineHeight: 24 }}>
                    {feature.desc}
                  </Text>
                </View>
              ))}
            </View>

            {/* Trust indicators */}
            <View
              style={{
                flexDirection: isXS ? 'column' : 'row',
                gap: isXS ? 12 : 32,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {['No data selling', 'No targeted ads', 'Delete anytime', 'You own your data'].map((item, index) => (
                <View key={index} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Icon name="check" size={14} color={subtleText} />
                  <Text size="n" color="secondary" weight="medium">{item}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>

        {/* ================================================================ */}
        {/* EVERY MODEL IN ONE PLACE */}
        {/* ================================================================ */}
        <View
          style={{
            width: '100%',
            alignItems: 'center',
            paddingHorizontal: sectionPadding,
            paddingVertical: sectionVertical,
          }}
        >
          <View style={{ maxWidth: 1000, width: '100%', alignItems: 'center' }}>
            <SectionHeading
              eyebrow="All Models, One Dashboard"
              title={"Stop Being Locked Into\nOne AI Provider"}
              subtitle="Why pay for ChatGPT, Claude, and Gemini separately? Gab AI gives you every top model in a single subscription. Switch between them freely — or let our smart routing pick the best one for each task."
              maxWidth={740}
            />

            {/* Highlight card */}
            <View
              style={{
                padding: isXS ? 24 : 28,
                borderRadius: 20,
                backgroundColor: cardBg,
                borderWidth: 1,
                borderColor: cardBorder,
                flexDirection: isXS ? 'column' : 'row',
                alignItems: 'center',
                gap: isXS ? 12 : 20,
                width: '100%',
                maxWidth: 700,
              }}
            >
              <View
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  backgroundColor: iconBoxBg,
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon name="arrows-rotate" size={20} color={iconColor} solid />
              </View>
              <Text
                size="m"
                style={{ textAlign: isXS ? 'center' : 'left', lineHeight: 26, flex: 1 }}
              >
                <Text weight="semibold">New models added automatically.</Text>
                {' '}When the next breakthrough AI drops, you don't need a new subscription — it's already in your dashboard.
              </Text>
            </View>
          </View>
        </View>

        {/* ================================================================ */}
        {/* THE ONLY AI THAT DOES EVERYTHING */}
        {/* ================================================================ */}
        <View
          style={{
            width: '100%',
            alignItems: 'center',
            paddingHorizontal: sectionPadding,
            paddingVertical: sectionVertical,
          }}
        >
          <View style={{ maxWidth: 1100, width: '100%', alignItems: 'center' }}>
            <SectionHeading
              eyebrow="All-in-One"
              title={"The Only AI That\nDoes Everything"}
              subtitle="Other platforms do text. Maybe images. Gab AI creates images, videos, music, and text — all in one conversation. No app switching. No extra subscriptions."
            />

            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: isXS ? 12 : 16,
                maxWidth: 800,
                marginBottom: isXS ? 40 : 56,
              }}
            >
              {ALL_IN_ONE_FEATURES.map((item) => (
                <View
                  key={item.label}
                  style={{
                    width: isXS ? '45%' : 160,
                    padding: isXS ? 16 : 24,
                    borderRadius: 16,
                    backgroundColor: cardBg,
                    borderWidth: 1,
                    borderColor: cardBorder,
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <View
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      backgroundColor: iconBoxBg,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon name={item.icon} size={20} color={iconColor} />
                  </View>
                  <Text size="s" weight="semibold" style={{ textAlign: 'center' }}>
                    {item.label}
                  </Text>
                  <Text size="xxs" color="tertiary" style={{ textAlign: 'center' }}>
                    {item.desc}
                  </Text>
                </View>
              ))}
            </View>

            <Button
              text="Try It All Free"
              variant="purple"
              size="large"
              isRound
              onPress={() => router.push('/signup')}
              style={{ paddingHorizontal: isXS ? 32 : 48 }}
            />
          </View>
        </View>

        {/* ================================================================ */}
        {/* COMPARISON TABLE */}
        {/* ================================================================ */}
        <View
          style={{
            width: '100%',
            alignItems: 'center',
            paddingHorizontal: sectionPadding,
            paddingVertical: sectionVertical,
          }}
        >
          <View style={{ maxWidth: 700, width: '100%', alignItems: 'center' }}>
            <SectionHeading
              eyebrow="The Comparison"
              title={"Gab AI vs. The Rest"}
              subtitle="See how Gab AI stacks up against ChatGPT, Claude, and Gemini."
              maxWidth={500}
            />

            <View
              style={{
                width: '100%',
                borderRadius: 20,
                overflow: 'hidden',
                backgroundColor: cardBg,
                borderWidth: 1,
                borderColor: cardBorder,
                marginBottom: isXS ? 48 : 64,
              }}
            >
              {/* Header row */}
              <View
                style={{
                  flexDirection: 'row',
                  paddingVertical: isXS ? 14 : 18,
                  paddingHorizontal: isXS ? 16 : 24,
                  borderBottomWidth: 1,
                  borderBottomColor: cardBorder,
                }}
              >
                <View style={{ flex: 1 }}>
                  <Text size="s" weight="semibold" color="secondary">Feature</Text>
                </View>
                <View style={{ width: isXS ? 70 : 100, alignItems: 'center' }}>
                  <Text size="s" weight="bold" style={{ color: '#8B5CF6' }}>Gab AI</Text>
                </View>
                <View style={{ width: isXS ? 70 : 100, alignItems: 'center' }}>
                  <Text size="s" weight="semibold" color="tertiary">Others</Text>
                </View>
              </View>

              {/* Data rows */}
              {COMPARISON_ROWS.map((row, index) => (
                <View
                  key={index}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingVertical: isXS ? 12 : 16,
                    paddingHorizontal: isXS ? 16 : 24,
                    borderBottomWidth: index < COMPARISON_ROWS.length - 1 ? 1 : 0,
                    borderBottomColor: cardBorder,
                  }}
                >
                  <View style={{ flex: 1 }}>
                    <Text size="s">{row.feature}</Text>
                  </View>
                  <View style={{ width: isXS ? 70 : 100, alignItems: 'center' }}>
                    <View
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 14,
                        backgroundColor: 'rgba(34, 197, 94, 0.15)',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon name="check" size={14} color="#22C55E" />
                    </View>
                  </View>
                  <View style={{ width: isXS ? 70 : 100, alignItems: 'center' }}>
                    <View
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 14,
                        backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon name="xmark" size={14} color={isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'} />
                    </View>
                  </View>
                </View>
              ))}
            </View>
          </View>
        </View>

        {/* ================================================================ */}
        {/* FINAL CTA */}
        {/* ================================================================ */}
        <View
          style={{
            width: '100%',
            alignItems: 'center',
            paddingHorizontal: sectionPadding,
            paddingVertical: sectionVertical,
          }}
        >
          <View
            style={{
              maxWidth: 720,
              width: '100%',
              padding: isXS ? 40 : 72,
              borderRadius: 24,
              backgroundColor: cardBg,
              borderWidth: 1,
              borderColor: cardBorder,
              alignItems: 'center',
            }}
          >
            <Text
              weight="bold"
              style={{
                textAlign: 'center',
                marginBottom: 16,
                fontSize: isXS ? 30 : 40,
                letterSpacing: -1,
                lineHeight: isXS ? 36 : 48,
              }}
            >
              Ready to Switch?
            </Text>
            <Text
              color="secondary"
              style={{
                textAlign: 'center',
                marginBottom: 40,
                maxWidth: 480,
                lineHeight: 26,
                fontSize: isXS ? 16 : 18,
              }}
            >
              Your conversations. Your memories. Your privacy. All in one AI that does everything — and never stops getting better.
            </Text>

            <View
              style={{
                flexDirection: isXS ? 'column' : 'row',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: isXS ? 10 : 16,
                marginBottom: 40,
                width: '100%',
                maxWidth: 540,
              }}
            >
              {[
                'Import conversations from Gab AI, GPT & Claude',
                'Transfer your AI memories instantly',
                'Every top model in one dashboard',
                'Anonymous, private, yours',
                'Images, video, music, and text',
                'Free to start',
              ].map((item, index) => (
                <View key={index} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, minWidth: 200 }}>
                  <Icon name="check" size={14} color={subtleText} />
                  <Text size="n">{item}</Text>
                </View>
              ))}
            </View>

            <View
              style={{
                flexDirection: isXS ? 'column' : 'row',
                gap: 12,
                width: '100%',
                maxWidth: 400,
              }}
            >
              <Button
                text="Switch to Gab AI"
                variant="purple"
                size="large"
                isRound
                isFullWidth
                prependIcon="arrow-right-to-bracket"
                iconIsSolid
                onPress={() => router.push('/signup')}
                style={{ height: isXS ? 52 : 56, flex: 1 }}
              />
              <Button
                text="View Pricing"
                variant="secondary"
                size="large"
                isRound
                isFullWidth={isXS}
                onPress={() => router.push('/pricing')}
                style={{
                  height: isXS ? 52 : 56,
                  paddingHorizontal: 28,
                  backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                }}
              />
            </View>

            <Text size="s" color="tertiary" style={{ marginTop: 20 }}>
              No credit card required
            </Text>
          </View>
        </View>

        <PublicFooter />
      </ScrollView>
    </View>
  );
}
